import { ASYNC_STATUS } from '@lib/constants'
import { useCancelableRequest } from '@lib/hooks'
import type { AppState } from '@store/types'
import { UserActions } from '@storeOld/reducers/user/actions'
import { useDispatch, useSelector } from 'react-redux'

import { STORAGE_LIMITS } from '@/constants/constants'
import { useAuth } from '@/hooks'
import { getCancelableUserStorageLimits } from '@/services/user'
import type { UserState } from '@/types'
import type { AttachmentItem } from '@/types/models'

const useUser = () => {
  const dispatch = useDispatch()
  const { authenticated } = useAuth()
  const user = useSelector<AppState, UserState>((state) => state.user)

  const [userStorageLimitsResponse, userStorageLimitsRequest] =
    useCancelableRequest(getCancelableUserStorageLimits)

  const getStorageLimits = async () => {
    if (!authenticated) {
      return
    }

    try {
      const { data } = await userStorageLimitsRequest()

      dispatch({
        type: UserActions.SET_STORAGE_LIMITS,
        payload: data || STORAGE_LIMITS,
      })
    } catch (e: unknown) {
      dispatch({
        type: UserActions.SET_STORAGE_LIMITS,
        payload: STORAGE_LIMITS,
      })
    }
  }

  const decreaseStorageLimits = (payload: AttachmentItem[]) => {
    dispatch({
      type: UserActions.DECREASE_STORAGE_LIMITS,
      payload,
    })
  }

  const increaseStorageLimits = (payload: AttachmentItem[]) => {
    dispatch({
      type: UserActions.INCREASE_STORAGE_LIMITS,
      payload,
    })
  }

  const isStorageLimitsPending =
    userStorageLimitsResponse.asyncStatus === ASYNC_STATUS.PENDING

  return {
    getStorageLimits,
    decreaseStorageLimits,
    increaseStorageLimits,
    isStorageLimitsPending,
    value: user,
  }
}

export default useUser
